import React, { useState, useEffect } from 'react';
import { X, Copy, Check, Loader2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Channel } from '../../types';
import { SocialMediaShare } from './SocialMediaShare';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  channels: Channel[];
  shareUrl?: string;
  isGenerating: boolean;
  error?: string;
  isExport?: boolean;
}

export const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  channels,
  shareUrl,
  isGenerating,
  error,
  isExport = false
}) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copied]);

  const handleCopy = async () => {
    if (shareUrl) {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
    }
  };

  // Website sharing details (for export modal)
  const websiteUrl = 'https://iptvplayer.stream';
  const websiteTitle = 'Check out this simple and awesome IPTV player!';
  const websiteDescription = 'A web-based IPTV player that lets you watch and manage your M3U playlists with ease.';

  // Playlist sharing details (for share modal)
  const playlistTitle = `Check out my IPTV playlist with ${channels.length} channels!`;
  const playlistDescription = `I've created a custom IPTV playlist with ${channels.length} channels. Check it out on IPTVPlayer.stream!`;

  // Use website details for export modal, playlist details for share modal
  const shareTitle = isExport ? websiteTitle : playlistTitle;
  const shareDescription = isExport ? websiteDescription : playlistDescription;
  const shareLink = isExport ? websiteUrl : (shareUrl || websiteUrl);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {isExport ? 'Share IPTV Player' : 'Share Your Playlist'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          {isExport ? (
            // Export mode - Show website sharing immediately
            <div className="py-8 text-center">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6">
                Share IPTVPlayer.stream on social media
              </h3>
              <div className="flex justify-center">
                <SocialMediaShare
                  url={websiteUrl}
                  title={websiteTitle}
                  description={websiteDescription}
                />
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-6">
                Help others discover IPTVPlayer.stream!
              </p>
            </div>
          ) : (
            // Share mode - Show playlist sharing with loading states
            <div className="space-y-6">
              {error ? (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              ) : isGenerating ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Generating share link...
                  </p>
                </div>
              ) : (
                <>
                  {/* Share URL Section */}
                  {shareUrl && (
                    <div className="space-y-4">
                      <div className="relative">
                        <input
                          type="text"
                          value={shareUrl}
                          readOnly
                          className="w-full pr-24 p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                        <button
                          onClick={handleCopy}
                          className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 px-3 py-1 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
                        >
                          {copied ? (
                            <>
                              <Check className="w-4 h-4 text-green-500" />
                              <span className="text-sm">Copied!</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4" />
                              <span className="text-sm">Copy</span>
                            </>
                          )}
                        </button>
                      </div>

                      {/* QR Code */}
                      <div className="flex flex-col items-center space-y-2 py-4">
                        <QRCodeSVG
                          value={shareUrl}
                          size={200}
                          level="H"
                          includeMargin
                          className="p-2 bg-white rounded-lg"
                        />
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Scan with your mobile device
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Social Media Share Section */}
                  <div className="space-y-4 text-center">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                      Share your playlist on social media
                    </h3>
                    <div className="flex justify-center">
                      <SocialMediaShare
                        url={shareLink}
                        title={shareTitle}
                        description={shareDescription}
                      />
                    </div>
                  </div>

                  {/* Additional Info */}
                  <div className="text-center space-y-1">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Sharing {channels.length} channel{channels.length !== 1 ? 's' : ''}
                    </p>
                    {shareUrl && (
                      <p className="text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                        ⚠️ This share link will be active until you create a new share
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 